import { MutableRefObject, useEffect, useRef, useReducer, useCallback } from 'react';

import { utilities } from 'core';
import * as useCases from 'core/use-cases';
import { useDispatch } from 'core/use-cases/utilities';

import { TooltipAction, type PositionX, type PositionY, type TooltipState } from './Tooltip.d';
import { getTooltipPosition } from './Tooltip.helpers';

const tooltipState: TooltipState = {
  isVisible: false,
  positionX: 'center',
  positionY: 'up',
};

const tooltipReducer = utilities?.createReducer<TooltipState>(
  {
    [TooltipAction.toggleVisibility]: (draft, { payload }) => {
      draft.isVisible = payload;
    },
    [TooltipAction.setPosition]: (draft, { payload }) => {
      const { positionX, positionY } = payload;

      draft.positionX = positionX;
      draft.positionY = positionY;
    },
  },
  tooltipState,
);

export const useTooltip = (align?: PositionX, direction?: PositionY) => {
  const containerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const tooltipRef = useRef() as MutableRefObject<HTMLDivElement>;
  const baseRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [state, dispatch] = useReducer(tooltipReducer, tooltipState);
  const appDispatch = useDispatch();
  const { isVisible, positionX, positionY } = state;

  const handleOnClick = useCallback(
    (label: string) => {
      if (!isVisible) {
        appDispatch(useCases.app.actions.onClickTooltip(label));
      }
    },

    [isVisible],
  );

  const handleVisibility = useCallback(() => {
    dispatch({ type: TooltipAction.toggleVisibility, payload: !isVisible });
  }, [isVisible]);

  const handlePosition = useCallback(() => {
    const tooltipClientRect = tooltipRef?.current?.getBoundingClientRect();
    const baseClientRect = baseRef?.current?.getBoundingClientRect();

    if (tooltipClientRect) {
      const tooltipPosition = getTooltipPosition(tooltipClientRect, baseClientRect, state);

      dispatch({
        type: TooltipAction.setPosition,
        payload: tooltipPosition,
      });
    }
  }, [state]);

  const handleInitialPosition = useCallback(() => {
    const tooltipPosition = { positionX: align, positionY: direction };

    dispatch({
      type: TooltipAction.setPosition,
      payload: tooltipPosition,
    });
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      /* istanbul ignore else */
      if (!containerRef?.current?.contains(event.target as Node)) {
        handleVisibility();
      }
    },
    [handleVisibility],
  );

  useEffect(() => {
    handlePosition();
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      window.addEventListener(
        'scroll',
        utilities.debounce(() => {
          handlePosition();
        }),
      );
      window.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('scroll', handlePosition);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, handleClickOutside, handlePosition]);

  useEffect(() => {
    handleInitialPosition();
  }, []);

  return {
    baseRef,
    containerRef,
    handleOnClick,
    handleVisibility,
    isVisible,
    positionX,
    positionY,
    tooltipRef,
  };
};
